import React, { useEffect, useState, useRef, useCallback } from "react";
import { Box, Skeleton, useTheme } from "@mui/material";
import { CustomTypography } from "../../custom-typography/custom-typography";
import { useStatisticsStore } from "../../../stores/statisticsStore";
import { BlockModalWrapper } from "../../../components/block-modal-wrapper/block-modal-wrapper";
import { FullScreenImage } from "../../chat/chat-bubble/image-messages/full-screen-image/full-screen-image";
import { AuthenticatedImage } from "../../authenticated-media/authenticated-image";
import { textStyle } from "./styles";

const ITEM_WIDTH = 170;
const ITEM_GAP = 16;
const ITEM_HEIGHT = 140;

export const AchievementCard: React.FC = () => {
  const { badges, fetchBadges, isLoadingBadges, errorBadges } =
    useStatisticsStore();
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState<string>("");
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const theme = useTheme();

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    fetchBadges();
  }, [fetchBadges]);

  const measureContainer = useCallback(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    measureContainer();
    window.addEventListener("resize", measureContainer);
    return () => window.removeEventListener("resize", measureContainer);
  }, [measureContainer]);

  const skeletonCount = (() => {
    if (containerWidth <= 0) return 1;
    const effectiveItemWidth = ITEM_WIDTH + ITEM_GAP;
    return Math.max(
      2,
      Math.floor((containerWidth + ITEM_GAP) / effectiveItemWidth)
    );
  })();

  const handleBadgeClick = (badgeUrl: string) => {
    setSelectedBadge(badgeUrl);
    setIsFullScreenOpen(true);
  };

  const handleCloseFullScreen = () => {
    setIsFullScreenOpen(false);
  };

  return (
    <BlockModalWrapper lable="Achievement">
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: `${ITEM_GAP}px`,
          flexWrap: "nowrap",
          overflowX: "auto",
        }}
      >
        {isLoadingBadges ? (
          Array.from({ length: skeletonCount }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              width={ITEM_WIDTH}
              height={ITEM_HEIGHT}
              animation="wave"
              sx={{ borderRadius: "8px" }}
            />
          ))
        ) : errorBadges ? (
          <CustomTypography color="red">{errorBadges}</CustomTypography>
        ) : badges.length === 0 ? (
          <CustomTypography sx={textStyle(theme)}>
            No achievements available
          </CustomTypography>
        ) : (
          badges.map((badgeUrl, index) => (
            <AuthenticatedImage
              key={index}
              src={badgeUrl}
              alt={`Achievement ${index + 1}`}
              sx={{
                background: "#f0f0f0",
                padding: "10px",
                height: `${ITEM_HEIGHT}px`,
                borderRadius: "8px",
                cursor: "pointer",
              }}
              onClick={() => handleBadgeClick(badgeUrl)}
            />
          ))
        )}
      </Box>
      <FullScreenImage
        open={isFullScreenOpen}
        onClose={handleCloseFullScreen}
        src={selectedBadge}
        alt="Achievement"
        enableDrawing={false}
      />
    </BlockModalWrapper>
  );
};

export default AchievementCard;
